// apps/patient/src/utils/onboarding.ts

import { getCurrentPlatform } from '../config/platforms';
import type { UserProfile } from '@prism-ehr/shared';

/**
 * Check if onboarding is complete based on current platform context
 * 
 * @param user - The user profile object (patient in PatientContext)
 * @returns boolean indicating if onboarding is complete
 */
export function isOnboardingComplete(user: any): boolean {
  if (!user) return false;
  
  const platform = getCurrentPlatform();
  
  switch (platform.id) {
    case 'exercise':
      return user.exercise_app?.onboarding_completed ?? false;
    case 'rehab':
      // ACL Care uses this platform
      return user.acl_care?.acl_onboarding_completed ?? false;
    default:
      // Generic/EHR platform uses top-level onboarding
      return user.onboarding?.is_completed ?? false;
  }
}

/**
 * Get redirect path if onboarding is incomplete
 * 
 * @param user - The user profile object
 * @returns Path to redirect to, or null if no redirect needed
 */
export function getOnboardingRedirectPath(user: any): string | null {
  if (!user) return '/login';
  if (!isOnboardingComplete(user)) return '/onboarding';
  return null;
}

/**
 * Check if user has active subscription or valid access for exercise app
 * 
 * @param user - The user profile object
 * @returns boolean indicating if user has exercise access
 */
export function hasExerciseAccess(user: any): boolean {
  if (!user?.exercise_app) return false;
  
  const status = user.exercise_app.subscription_status;
  
  // Active statuses
  if (['active', 'trial', 'access_code'].includes(status)) {
    // Check expiration for trial
    if (status === 'trial' && user.exercise_app.trial_expires_at) {
      return new Date(user.exercise_app.trial_expires_at) > new Date();
    }
    // Check expiration for subscription
    if (user.exercise_app.subscription_expires_at) {
      return new Date(user.exercise_app.subscription_expires_at) > new Date();
    }
    return true;
  }
  
  return false;
}

/**
 * Determine user access type for exercise platform
 * 
 * @param user - The user profile object (includes nested patient)
 * @returns Access type: 'standalone', 'provider_linked', or 'hybrid'
 */
export function getExerciseAccessType(
  user: any
): 'standalone' | 'provider_linked' | 'hybrid' {
  if (!user) return 'standalone';
  
  // patient.patient is the actual patient record
  const patientRecord = user.patient;
  const isProviderLinked = !!(patientRecord?.organization_id || patientRecord?.provider_id);
  const hasPersonalAccess = hasExerciseAccess(user);
  
  if (isProviderLinked && hasPersonalAccess) {
    return 'hybrid';
  } else if (isProviderLinked) {
    return 'provider_linked';
  }
  
  return 'standalone';
}

/**
 * Get the appropriate default route based on platform and user state
 * 
 * @param user - The user profile object
 * @returns Default route path
 */
export function getDefaultRoute(user: any): string {
  const platform = getCurrentPlatform();
  
  if (platform.id === 'exercise') {
    // For exercise app, go to programs/workouts
    return '/programs';
  }
  
  return '/dashboard';
}

/**
 * Check if user needs to complete payment before using the app
 * 
 * @param user - The user profile object  
 * @returns boolean indicating if payment is required
 */
export function needsPayment(user: any): boolean {
  if (!user) return false;
  
  const platform = getCurrentPlatform();
  
  if (platform.id === 'exercise') {
    const status = user.exercise_app?.subscription_status;
    // No payment needed if active, on trial, or has access code
    if (['active', 'trial', 'access_code'].includes(status)) {
      return false;
    }
    // Payment needed if status is none, past_due, or cancelled
    return true;
  }
  
  return false;
}